/**
 * Make Recurring Dialog
 *
 * Turns an existing invoice into a recurring billing schedule. Everything except
 * the cadence (account, business profile, contract, payment terms, tax rate and
 * every line item) is copied server-side, so this dialog only asks the four
 * questions that can't be inferred: how often, which day, starting when, until when.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Repeat, CalendarClock, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly', months: 1, every: 'month' },
  { value: 'quarterly', label: 'Quarterly', months: 3, every: '3 months' },
  { value: 'semi_annual', label: 'Semi-Annual', months: 6, every: '6 months' },
  { value: 'annual', label: 'Annual', months: 12, every: 'year' },
] as const;

interface MakeRecurringDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber: number;
  accountName: string;
  issueDate: string;
  total: number;
  itemCount: number;
  paymentTerms: string | null;
}

/** Parse a yyyy-mm-dd (or ISO) string into a UTC-safe date */
function toUtcDate(value: string): Date {
  const iso = value.split('T')[0];
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
}

function toInputValue(date: Date): string {
  return date.toISOString().split('T')[0];
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * Mirrors the server's next_billing_date calculation: the next occurrence of the
 * billing day on or after the start date, clamped to the length of the month.
 */
function firstBillingDate(startDate: string, billingDay: number): Date | null {
  if (!startDate) return null;
  const start = toUtcDate(startDate);
  if (isNaN(start.getTime())) return null;

  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();
  if (start.getUTCDate() > billingDay) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return new Date(Date.UTC(year, month, Math.min(billingDay, daysInMonth(year, month))));
}

function addMonthsClamped(date: Date, months: number, billingDay: number): Date {
  let year = date.getUTCFullYear();
  let month = date.getUTCMonth() + months;
  year += Math.floor(month / 12);
  month = ((month % 12) + 12) % 12;
  return new Date(Date.UTC(year, month, Math.min(billingDay, daysInMonth(year, month))));
}

export function MakeRecurringDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  accountName,
  issueDate,
  total,
  itemCount,
  paymentTerms,
}: MakeRecurringDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const defaultStart = issueDate ? toInputValue(toUtcDate(issueDate)) : toInputValue(new Date());
  const defaultDay = issueDate ? toUtcDate(issueDate).getUTCDate() : 1;

  const [frequency, setFrequency] = useState<string>('monthly');
  const [billingDay, setBillingDay] = useState<number>(defaultDay);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState('');
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [name, setName] = useState(`${accountName} — Monthly`);
  const [nameTouched, setNameTouched] = useState(false);
  const [createdScheduleId, setCreatedScheduleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const freq = FREQUENCIES.find((f) => f.value === frequency) || FREQUENCIES[0];

  const upcoming = useMemo(() => {
    const first = firstBillingDate(startDate, billingDay);
    if (!first) return [];
    const dates = [first];
    for (let i = 1; i < 3; i++) {
      dates.push(addMonthsClamped(first, freq.months * i, billingDay));
    }
    const limit = endDate ? toUtcDate(endDate) : null;
    return limit ? dates.filter((d) => d <= limit) : dates;
  }, [startDate, billingDay, freq.months, endDate]);

  function handleFrequencyChange(value: string) {
    setFrequency(value);
    if (!nameTouched) {
      const label = FREQUENCIES.find((f) => f.value === value)?.label || 'Monthly';
      setName(`${accountName} — ${label}`);
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // The API client resolves (rather than throws) on error responses
      const result = await api.makeInvoiceRecurring(invoiceId, {
        name: name.trim() || null,
        frequency,
        billingDayOfMonth: billingDay,
        startDate,
        endDate: endDate || null,
        autoGenerate,
        linkSourceInvoice: true,
        copyNotes: true,
      });
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create the recurring schedule.');
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['recurringSchedules'] });
      queryClient.invalidateQueries({ queryKey: ['dueSchedules'] });
      setCreatedScheduleId(result?.data?.scheduleId ?? null);
      setError(null);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to create the recurring schedule.';
      setError(message);
    },
  });

  function handleClose(next: boolean) {
    if (!next) {
      // Reset for the next time it opens
      setCreatedScheduleId(null);
      setError(null);
      mutation.reset();
    }
    onOpenChange(next);
  }

  // ---- Success state ----------------------------------------------------
  if (createdScheduleId) {
    const first = upcoming[0];
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Recurring schedule created
            </DialogTitle>
            <DialogDescription>
              Invoice #{invoiceNumber} is now the first invoice of <strong>{name}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Bills</span>
              <span className="font-medium">Every {freq.every}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Next invoice</span>
              <span className="font-medium">{first ? formatDate(first.toISOString()) : '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-medium">{formatCurrency(total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Generation</span>
              <span className="font-medium">{autoGenerate ? 'Automatic' : 'Manual review'}</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleClose(false)}>
              Done
            </Button>
            <Button
              onClick={() => {
                handleClose(false);
                navigate(`/billing/recurring/${createdScheduleId}`);
              }}
            >
              View schedule
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- Form state -------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Repeat className="h-5 w-5" />
            Make this invoice recurring
          </DialogTitle>
          <DialogDescription>
            {accountName} · {itemCount} line item{itemCount === 1 ? '' : 's'} ·{' '}
            {formatCurrency(total)}
            {paymentTerms ? ` · ${paymentTerms}` : ''} will be copied into a reusable billing
            template. Just choose how often it should bill.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Bills every *</Label>
              <Select value={frequency} onValueChange={handleFrequencyChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>On day *</Label>
              <Select value={String(billingDay)} onValueChange={(v) => setBillingDay(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      {day === 31 ? 'Last day of month' : `Day ${day}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Starting *</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Ends</Label>
              <Input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to bill indefinitely</p>
            </div>
          </div>

          <div>
            <Label>Schedule name</Label>
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
              placeholder={`${accountName} — ${freq.label}`}
            />
          </div>

          <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30">
            <input
              type="checkbox"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
              className="h-4 w-4 mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">Create invoices automatically</span>
              <span className="block text-muted-foreground text-xs mt-0.5">
                Each invoice is created as a draft on its billing date. Turn this off to review and
                generate them by hand from the Generate Invoices tab.
              </span>
            </span>
          </label>

          {/* Live preview of the resulting cadence */}
          {upcoming.length > 0 && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                <CalendarClock className="h-3.5 w-3.5" />
                Upcoming invoices
              </div>
              <div className="space-y-1 text-sm">
                {upcoming.map((d, idx) => (
                  <div key={d.toISOString()} className="flex justify-between">
                    <span className={idx === 0 ? 'font-medium' : 'text-muted-foreground'}>
                      {formatDate(d.toISOString())}
                      {idx === 0 && <span className="text-xs text-muted-foreground ml-2">next</span>}
                    </span>
                    <span className={idx === 0 ? 'font-medium' : 'text-muted-foreground'}>
                      {formatCurrency(total)}
                    </span>
                  </div>
                ))}
                {!endDate && <div className="text-xs text-muted-foreground pt-1">…and so on</div>}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !startDate}>
              {mutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Repeat className="h-4 w-4 mr-2" />
              )}
              Create schedule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
